import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { AIGeneratorDialog } from './AIGeneratorDialog';
import { generateAIContent } from '../../api/ai/generator';

/**
 * Generic AI Generator Component
 * 
 * @param {Object} props
 * @param {string} props.module - Module name (e.g., 'hse_document')
 * @param {Object} props.context - Context fields to include in prompt
 * @param {Array<string>} props.fieldsToGenerate - Fields to generate
 * @param {Function} props.onGenerate - Callback when content is generated (receives generatedFields object)
 * @param {Object} props.config - Module configuration from aiGeneratorConfigs
 * @param {string} props.buttonLabel - Custom button label (optional)
 * @param {string} props.className - Additional CSS classes (optional)
 */
export const AIGenerator = ({ 
  module, 
  context = {}, 
  fieldsToGenerate = [], 
  onGenerate,
  config,
  buttonLabel,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleGenerate = async (options) => {
    if (!module) {
      toast.error(t("Modul ikke spesifisert"));
      return;
    }
    
    if (!fieldsToGenerate || fieldsToGenerate.length === 0) {
      toast.error(t("Ingen felter spesifisert for generering"));
      return;
    }
    
    setLoading(true);
    try {
      const result = await generateAIContent({
        module,
        context,
        fields_to_generate: fieldsToGenerate,
        provider: options.provider || 'chatgpt',
        options: options.options || {}
      });
      
      if (result.success) {
        onGenerate?.(result.generated_fields, {
          provider: options?.provider,
          generationOptions: options?.options || {},
        });
        toast.success(t("Innhold generert med AI"));
      } else {
        const errorMessage = result.errors?.[0] || t("Kunne ikke generere innhold");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error generating AI content:", error);
      const errorMessage = error.response?.data?.errors?.[0] || 
                          error.response?.data?.error ||
                          error.message || 
                          t("Kunne ikke generere innhold");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };
  
  if (!config) {
    console.warn(`No config found for module: ${module}`);
    return null;
  }
  
  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        disabled={loading}
        className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        title={t("Generer innhold med AI")}
      >
        <SparklesIcon className="h-4 w-4 mr-2 text-indigo-600" />
        {loading ? t("Genererer...") : (buttonLabel || config.buttonLabel || t("Generer med AI"))}
      </button>
      
      <AIGeneratorDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onGenerate={handleGenerate}
        module={module}
        context={context}
        fieldsToGenerate={fieldsToGenerate}
        config={config}
        loading={loading}
      />
    </>
  );
};
